import { useCallback, useEffect, useState } from 'react';
import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { X } from 'lucide-react';
import { notificationIconForType } from '@/utils/notificationDisplay';

const EXIT_DURATION_MS = 220;

export type BannerNotification = {
  id: string;
  title: string;
  message: string;
  type: string;
};

type Props = {
  notification: BannerNotification;
  /** How long before it leaves on its own. Longer than a Toast: this one is actionable. */
  duration?: number;
  onOpen: () => void;
  onClose: () => void;
};

/**
 * The in-app arrival banner.
 *
 * Its own component rather than the shared Toast: this one carries a title and a body,
 * has an icon that varies by notification type, and is tappable -- Toast is a one-line
 * strip that dismisses.
 *
 * Shown only when the app is on screen and the message is NOT one the phone was told
 * about. The fan-out already withholds the push from anyone whose client says it is
 * awake (see 20260910120000_chat_presence.sql), so this is what fills that silence.
 */
export function NotificationBanner({ notification, duration = 5200, onOpen, onClose }: Props) {
  const [isExiting, setIsExiting] = useState(false);
  const { Icon, color } = notificationIconForType(notification.type);

  const close = useCallback(() => {
    if (isExiting) return;
    setIsExiting(true);
    setTimeout(onClose, EXIT_DURATION_MS);
  }, [onClose, isExiting]);

  useEffect(() => {
    const timer = setTimeout(close, duration);
    return () => clearTimeout(timer);
  }, [duration, close]);

  return (
    <Flex
      role="button"
      tabIndex={0}
      aria-label={`${notification.title}: ${notification.message}`}
      position="fixed"
      top="calc(env(safe-area-inset-top, 0px) + 1rem)"
      right={{ base: 3, md: 5 }}
      left={{ base: 3, md: 'auto' }}
      maxW={{ md: '420px' }}
      p={3}
      gap={3}
      align="center"
      borderRadius="xl"
      borderWidth={1}
      borderColor="volyx.borderLight"
      bg="volyx.bg"
      boxShadow="0 10px 30px rgba(0,0,0,0.18)"
      zIndex={10001}
      cursor="pointer"
      transition="all 0.22s ease-out"
      animation={
        isExiting ? 'slideOutRight 0.22s ease-out forwards' : 'slideInFromRight 0.3s ease-out'
      }
      onClick={() => {
        onOpen();
        close();
      }}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        onOpen();
        close();
      }}
    >
      <Flex
        w="36px"
        h="36px"
        flexShrink={0}
        align="center"
        justify="center"
        borderRadius="full"
        bg="volyx.lightGray"
        color={color}
      >
        <Icon size={18} />
      </Flex>

      <Box minW={0} flex={1}>
        <Text fontSize="sm" fontWeight="semibold" color="volyx.primary" lineClamp={1}>
          {notification.title}
        </Text>
        <Text fontSize="xs" color="volyx.textSecondary" lineClamp={2}>
          {notification.message}
        </Text>
      </Box>

      <chakra.button
        type="button"
        aria-label="Dismiss"
        flexShrink={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
        w="28px"
        h="28px"
        borderRadius="full"
        color="volyx.textSecondary"
        bg="transparent"
        cursor="pointer"
        _hover={{ bg: 'volyx.lightGray' }}
        onClick={(e) => {
          // Dismissing must not also open the thread the card points at.
          e.stopPropagation();
          close();
        }}
      >
        <X size={15} />
      </chakra.button>
    </Flex>
  );
}
